import { createAction } from '@reduxjs/toolkit'
import { all, call, delay, put, select, takeEvery } from 'redux-saga/effects'

import { addNewDrawableLocationToStore, generateMeasurementsToUpdate } from './createDrawableLocation'
import createPathItemFromType from './createPathItemFromType'
import addMetadataToPath from './data-prep/addMetadataToPath'
import { selectPastedMaterialAction } from './selectPastedMaterial'
import { UPDATE_OPENING_GROUPS_SUCCESS } from '../../../actions/drawable'
import { addOpeningLocation, bulkCreateOpenings, CreateOpeningResponse } from '../../../api/projects-api'
import { Coordinate } from '../../../models/activeDrawable'
import { GeneralDrawableSettings } from '../../../models/activeDrawableSettings'
import { ActiveFloor } from '../../../models/activeFloor'
import { Building } from '../../../models/projectBuilding'
import { DEFAULT_SCALE_FACTOR } from '../../../shared/constants/scales'
import { convertScaleFactorLabelEnumToDecimal } from '../../../utils/calculations/scaleConversion/scaleConversion'
import { getUnitOfMeasureByDrawingType } from '../../../utils/general'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Color, Select, Workspace } from '../../lib/toolBoxes/2D'
import { copyDrawables, getCurrentDrawableLocationsFromRootState } from '../../slices/2D'
import { selectActiveBuildingFromActiveFloor } from '../../slices/buildings'
import { updateToolbarMessage } from '../../slices/common'
import { selectDrawableActiveFloor } from '../../slices/documents'
import {
    ActiveGeometryGroup,
    GeometricDrawable,
    GeometryGroup,
    selectDrawableGroupsGeometriesHash,
} from '../../slices/geometry'
import { selectProjectId } from '../../slices/project'
import {
    BulkOpeningCreationData,
    ERROR_PASTING_MATERIAL_MESSAGE,
    IMUP2DDrawableCutout,
    IMUP2DDrawableLocation,
    PASTING_MATERIAL_MESSAGE,
    TOOLBAR_MESSAGE_TIMER,
} from '../../types'

export const pasteMaterialAction = createAction<number[]>('pasteMaterial')

export const TEMPORARY_DRAWABLE_ID = -1

export function* createPathAndAddMetadataFromInitial(
    location: IMUP2DDrawableLocation,
    areaOpacity: number,
    lineOpacity: number,
    shapeColor: paper.Color,
    regionPaths: paper.Path[]
) {
    const path: paper.Path = yield call(
        createPathItemFromType,
        location,
        location.additionalData.initial_coordinates,
        areaOpacity,
        lineOpacity,
        shapeColor,
        regionPaths
    )

    yield call(addMetadataToPath, path, '', location)
    path.data.drawable_id = TEMPORARY_DRAWABLE_ID

    return path
}

export function* createPathAndAddMetadata(
    location: IMUP2DDrawableLocation,
    areaOpacity: number,
    lineOpacity: number,
    shapeColor: paper.Color,
    regionPaths: paper.Path[]
) {
    const path: paper.Path = yield call(
        createPathItemFromType,
        location,
        location.coordinates,
        areaOpacity,
        lineOpacity,
        shapeColor,
        regionPaths
    )

    yield call(addMetadataToPath, path, '', location)
    path.data.drawable_id = TEMPORARY_DRAWABLE_ID

    return path
}

export function buildBulkOpeningMeasurementInput(
    location: IMUP2DDrawableLocation & { scaleFactor: number },
    shiftedCoordinates: Coordinate[],
    path: paper.Path,
    activeFloor: ActiveFloor,
    shiftedCutouts?: IMUP2DDrawableCutout[]
) {
    return {
        coordinates: shiftedCoordinates,
        cutouts: shiftedCutouts,
        path,
        settingsAndType: {
            type: location.drawing_type,
            settings: location.settings as GeneralDrawableSettings,
        },
        scaleFactor: location.scaleFactor,
        pdfScale: activeFloor.document_chunk.pdf_scale || 1,
        xCalibrationFactor: activeFloor.document_chunk.calibration_factor_x,
        yCalibrationFactor: activeFloor.document_chunk.calibration_factor_y,
        dpi: activeFloor.document_chunk.dpi ?? null,
        initialCoordinates: undefined,
        initialPathLength: undefined,
    }
}

export function* updateStoreWithDrawable(drawable: CreateOpeningResponse, drawableGroup: GeometryGroup) {
    const drawableWithDoneState: GeometricDrawable = {
        ...drawable,
        isVisible: true,
    }

    const isDrawableInOpenings = drawableGroup?.openings.find((op) => op.id === drawable.id)

    const newActiveDrawableGroup = isDrawableInOpenings
        ? drawableGroup
        : {
              ...drawableGroup,
              openings: drawableGroup?.openings
                  ? drawableGroup?.openings.concat([drawableWithDoneState])
                  : [drawableWithDoneState],
              settings: drawable.opening_group.settings,
          }

    yield put({
        type: UPDATE_OPENING_GROUPS_SUCCESS,
        payload: {
            openingGroups: {
                newGroup: newActiveDrawableGroup,
                originalGroup: drawableGroup,
            },
        },
    })

    return newActiveDrawableGroup
}

export function* updateDrawableLocationsAndOpeningGroups(
    drawable: CreateOpeningResponse,
    oldDrawableGroup?: GeometryGroup,
    newGroup?: GeometryGroup,
    oldDrawableId?: number,
    oldLocationId?: number
) {
    const oldDrawable = oldDrawableGroup?.openings.find((drawable) => drawable.id === oldDrawableId)
    const group = newGroup ? newGroup : oldDrawableGroup

    if (!group) return

    if (!oldDrawableId || oldDrawable?.opening_locations.length === 1) {
        yield call(addNewDrawableLocationToStore, drawable, group.id)

        return yield call(updateStoreWithDrawable, drawable, group)
    } else if (oldLocationId && oldDrawable && oldDrawable?.opening_locations.length > 1) {
        const opLocationsToCreate = oldDrawable.opening_locations.filter(
            (opLoc) => opLoc.id !== oldLocationId && !opLoc.additional_data?.joist_length
        )

        let latestDrawable = drawable

        for (const opLoc of opLocationsToCreate) {
            latestDrawable = yield call(
                addOpeningLocation,
                drawable.id,
                opLoc.coordinates,
                drawable.opening_locations[0].document_chunk_id,
                drawable.project_id,
                drawable.opening_locations[0].region_id,
                opLoc.additional_data
            )
        }

        return yield call(updateStoreWithDrawable, latestDrawable, group)
    } else if (!oldDrawable) {
        yield call(addNewDrawableLocationToStore, drawable, group.id)

        return yield call(updateStoreWithDrawable, drawable, { ...group, openings: [] })
    }
}

function determineGroupForResponse(
    res: CreateOpeningResponse,
    drawableGroups: Record<string, GeometryGroup>,
    newMatGroups: ActiveGeometryGroup[]
): GeometryGroup {
    const newGroup = newMatGroups.find((group) => group.id === res.opening_group_id)

    if (drawableGroups[res.opening_group_id]) {
        return drawableGroups[res.opening_group_id]
    } else if (newGroup) {
        return {
            ...newGroup,
            drawablesPerTab: [],
            isActive: {},
            openings: [...newGroup.openings, { ...res, isVisible: true } as GeometricDrawable],
        }
    }

    return {
        ...res.opening_group,
        isActive: {},
        drawablesPerTab: [],
        openings: [{ ...res, isVisible: true } as GeometricDrawable],
    }
}

export function shiftLocationCutouts(cutouts: IMUP2DDrawableCutout[] | undefined, xOffset: number) {
    if (!cutouts || cutouts.length === 0) {
        return [] as IMUP2DDrawableCutout[]
    }
    const clonedCutouts = structuredClone(cutouts)

    clonedCutouts.forEach((cutout) => {
        cutout.coordinates = shiftLocationCoordinates(cutout.coordinates, xOffset)
    })

    return clonedCutouts
}

export function shiftLocationCoordinates(coordinates: Coordinate[], xOffset: number) {
    return coordinates.map((coord) => {
        return [coord[0] + xOffset, coord[1]]
    }) as Coordinate[]
}

export function* handlePasteMaterial(action: ReturnType<typeof pasteMaterialAction>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const [selectTool, workspaceTool, colorTool]: [Select, Workspace, Color] = yield call(manager.getTools, [
        Select.NAME,
        Workspace.NAME,
        Color.NAME,
    ])

    const activeFloor: ActiveFloor | null = yield select(selectDrawableActiveFloor)

    const activeBuilding: Building | undefined = yield select(selectActiveBuildingFromActiveFloor)

    if (!activeFloor || !activeBuilding) return

    const projectId: number | null = yield select(selectProjectId)

    if (!projectId) return

    yield put(updateToolbarMessage(PASTING_MATERIAL_MESSAGE))

    const rectangleWidth: number = yield call(selectTool.extractMultiSelectRectangleWidth.bind(selectTool))

    yield call(selectTool.clearAllMultiSelections)
    yield put(copyDrawables([]))

    const locations: IMUP2DDrawableLocation[] = yield select(getCurrentDrawableLocationsFromRootState)

    const drawableGroups: Record<string, GeometryGroup> = yield select(selectDrawableGroupsGeometriesHash, true)

    const regionPaths: paper.Path[] = yield call(workspaceTool.getAllRegionItems)

    const mapOfLocationByOpeningLocationIds: Map<number, IMUP2DDrawableLocation> = new Map()

    locations.forEach((l) => mapOfLocationByOpeningLocationIds.set(l.opening_location_id, l))

    const openingIMUPLocations: (IMUP2DDrawableLocation & { scaleFactor: number })[] = yield action.payload.reduce(
        (accLocations, locationId) => {
            const location = mapOfLocationByOpeningLocationIds.get(locationId)

            if (location) {
                // Determine if this drawable is inside a region so we can scale it appropriately
                let thisRegionGroup: paper.Path | null = null

                for (const regionPath of regionPaths) {
                    for (const c of location.coordinates) {
                        if (!regionPath.contains(workspaceTool.generatePoint(c))) {
                            break
                        }
                        thisRegionGroup = regionPath
                    }
                }
                const scaleFactor = thisRegionGroup
                    ? thisRegionGroup.data.scale
                    : activeFloor?.scale_factor ?? DEFAULT_SCALE_FACTOR

                accLocations.push({
                    ...location,
                    region_id: thisRegionGroup ? thisRegionGroup.data.region_id : thisRegionGroup,
                    scaleFactor: convertScaleFactorLabelEnumToDecimal(scaleFactor),
                })
            }

            return accLocations
        },
        [] as (IMUP2DDrawableLocation & { scaleFactor: number })[]
    )

    const shapeColor = colorTool.createColor('#FFFFFF')
    const paths: paper.Path[] = yield all(
        openingIMUPLocations.map((loc) => {
            return createPathAndAddMetadata(loc, 0, 0, shapeColor, regionPaths)
        })
    )

    let pastingToNewBuilding = false
    const { bulkOpeningsToCreate } = openingIMUPLocations.reduce(
        (openingsData, location, i) => {
            const path = paths[i]
            const shiftedCoordinates = shiftLocationCoordinates(location.coordinates, rectangleWidth / 2)
            const shiftedCutouts = shiftLocationCutouts(location.cutouts, rectangleWidth / 2)
            const opening = drawableGroups[location.opening_group_id].openings.filter(
                (o) => o.id === location.opening_id
            )
            const locationCreationData = {
                opening_group_id: location.opening_group_id,
                opening_id: location.opening_id,
                type: drawableGroups[location.opening_group_id].type,
                coordinates: shiftedCoordinates,
                cutouts: shiftedCutouts,
                document_chunk_id: activeFloor.document_chunk.id,
                measurements: generateMeasurementsToUpdate(
                    buildBulkOpeningMeasurementInput(location, shiftedCoordinates, path, activeFloor, shiftedCutouts)
                ),
                settings: {
                    unit_of_measure: getUnitOfMeasureByDrawingType(path.data.shapeType),
                    shape_type: path.data.shapeType,
                    source_measurement_id: opening[0]?.settings?.source_measurement_id ?? null,
                },
                region_id: location.region_id,
                additional_data:
                    location.additionalData && Object.keys(location.additionalData).length > 0
                        ? {
                              ...location.additionalData,
                              initial_coordinates: location.additionalData.initial_coordinates
                                  ? shiftLocationCoordinates(
                                        location.additionalData.initial_coordinates,
                                        rectangleWidth / 2
                                    )
                                  : shiftLocationCoordinates(location.coordinates, rectangleWidth / 2),
                          }
                        : {},
                ai_suggestion_id: path.data.aiSuggestion?.id,
                floor_hash: activeFloor.hash,
                oldDrawableId: location.drawable_id,
                oldLocationId: location.opening_location_id,
            }

            if (!!drawableGroups[location.opening_group_id]) {
                openingsData.bulkOpeningsToCreate.push(locationCreationData)
            }

            if (location.settings.rcm_building_id !== activeBuilding.rcm_id) {
                pastingToNewBuilding = true
            }

            return openingsData
        },
        {
            bulkOpeningsToCreate: [] as (BulkOpeningCreationData & { oldDrawableId: number; oldLocationId: number })[],
        }
    )

    yield all(paths.map((path) => call([path, path.remove])))

    const newMatGroups: ActiveGeometryGroup[] = []

    try {
        if (bulkOpeningsToCreate.length > 0) {
            const response: CreateOpeningResponse[] = yield call(
                bulkCreateOpenings,
                projectId,
                bulkOpeningsToCreate.map((data) => ({ ...data, oldDrawableId: undefined, oldLocationId: undefined })),
                pastingToNewBuilding ? activeBuilding.rcm_id : undefined
            )

            for (const [i, res] of response.entries()) {
                newMatGroups.push(
                    yield call(
                        updateDrawableLocationsAndOpeningGroups,
                        res,
                        drawableGroups[bulkOpeningsToCreate[i].opening_group_id],
                        determineGroupForResponse(res, drawableGroups, newMatGroups),
                        bulkOpeningsToCreate[i].oldDrawableId,
                        bulkOpeningsToCreate[i].oldLocationId
                    )
                )
            }
        }

        yield put(updateToolbarMessage(null))

        const newMatDrawableIds = new Set(
            newMatGroups.flatMap((group) =>
                group.openings.reduce((openingIds, opening) => {
                    const originalGroupOpeningIds = drawableGroups[group.id]?.openings.map((op) => op.id)

                    if (!originalGroupOpeningIds || !originalGroupOpeningIds.includes(opening.id)) {
                        openingIds.push(opening.id)
                    }

                    return openingIds
                }, [] as number[])
            )
        )

        yield put(selectPastedMaterialAction([...newMatDrawableIds]))
    } catch (e) {
        console.error(e)
        yield put(updateToolbarMessage(ERROR_PASTING_MATERIAL_MESSAGE))
        yield delay(TOOLBAR_MESSAGE_TIMER)
        yield put(updateToolbarMessage(null))
    }
}

export function* watchForPasteMaterial() {
    yield takeEvery(pasteMaterialAction.type, handlePasteMaterial)
}
